import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Badge from '@/components/base/Badge';
import Modal from '@/components/base/Modal';
import BillingContactsCard from '@/pages/clients/detail/components/BillingContactsCard';

interface Cliente {
  id: number;
  company_name: string;
  rfc: string;
  address: string;
  credit_status: string;
  credit_limit: number;
  current_balance: number;
  status: string;
  requires_invoice: boolean;
  created_at: string;
}

interface Contacto {
  id: number;
  client_id: number;
  name: string;
  position: string;
  department: string;
  phone: string;
  email: string;
}

interface FacturaCliente {
  id: number;
  folio: string;
  issue_date: string;
  due_date: string;
  total_amount: number;
  pending_balance: number;
  status: string;
  sent_at: string | null;
}

interface PagoCliente {
  id: number;
  invoice_id: number;
  amount: number;
  payment_method: string;
  reference: string;
  payment_date: string;
  invoice_folio?: string;
}

export default function ClienteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [contactos, setContactos] = useState<Contacto[]>([]);
  const [facturas, setFacturas] = useState<FacturaCliente[]>([]);
  const [pagos, setPagos] = useState<PagoCliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'contactos' | 'historial'>('contactos');
  const [showContactModal, setShowContactModal] = useState(false);
  const [nuevoContacto, setNuevoContacto] = useState({ nombre: '', cargo: '', departamento: 'Finanzas', telefono: '', email: '' });
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState('');

  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editForm, setEditForm] = useState({
    company_name: '',
    rfc: '',
    calle: '',
    ciudad: '',
    estado: '',
    cp: '',
    credit_status: 'none' as 'none' | '30days',
    credit_limit: '0',
    requires_invoice: true,
  });
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  useEffect(() => {
    if (id) fetchCliente();
  }, [id]);

  async function fetchCliente() {
    try {
      const { data: cli, error: cliErr } = await supabase
        .from('erp_clients')
        .select('*')
        .eq('id', Number(id))
        .maybeSingle();

      if (cliErr) throw cliErr;
      if (!cli) { setLoading(false); return; }
      setCliente(cli);

      const [contRes, factRes] = await Promise.all([
        supabase.from('erp_contacts').select('*').eq('client_id', cli.id),
        supabase.from('erp_invoices').select('*').eq('client_id', cli.id).order('issue_date', { ascending: false }),
      ]);

      if (contRes.error) throw contRes.error;
      if (factRes.error) throw factRes.error;

      const facturasData = factRes.data || [];
      setContactos(contRes.data || []);
      setFacturas(facturasData);

      if (facturasData.length > 0) {
        const invoiceIds = facturasData.map(f => f.id);
        const { data: pagosData, error: pagosErr } = await supabase
          .from('erp_payments')
          .select('*')
          .in('invoice_id', invoiceIds)
          .order('payment_date', { ascending: false });

        if (!pagosErr && pagosData) {
          const folioMap = new Map(facturasData.map(f => [f.id, f.folio]));
          const pagosEnriched = pagosData.map(p => ({
            ...p,
            invoice_folio: folioMap.get(p.invoice_id) || 'N/A',
          }));
          setPagos(pagosEnriched);
        }
      }
    } catch (err) {
      console.error('Error fetching client:', err);
    } finally {
      setLoading(false);
    }
  }

  function parseAddress(addr: string | null) {
    if (!addr) return { calle: '', ciudad: '', estado: '', cp: '' };
    const parts = addr.split(',').map(p => p.trim());
    const cpMatch = parts.find(p => p.toUpperCase().startsWith('C.P.'));
    const cp = cpMatch ? cpMatch.replace(/C\.P\.\s*/i, '').trim() : '';
    const filtered = parts.filter(p => !p.toUpperCase().startsWith('C.P.'));
    const calle = filtered[0] || '';
    const ciudad = filtered.length >= 2 ? filtered[filtered.length - 2] : '';
    const estado = filtered.length >= 1 ? filtered[filtered.length - 1] : '';
    return { calle, ciudad, estado, cp };
  }

  function openEditModal() {
    if (!cliente) return;
    const addr = parseAddress(cliente.address);
    setEditForm({
      company_name: cliente.company_name,
      rfc: cliente.rfc || '',
      calle: addr.calle,
      ciudad: addr.ciudad,
      estado: addr.estado,
      cp: addr.cp,
      credit_status: (cliente.credit_status as 'none' | '30days') || 'none',
      credit_limit: String(cliente.credit_limit || 0),
      requires_invoice: cliente.requires_invoice ?? true,
    });
    setEditError('');
    setEditSuccess('');
    setShowEditClientModal(true);
  }

  async function handleEditClient() {
    setEditError('');
    setEditSuccess('');

    if (!cliente) return;
    if (!editForm.company_name.trim() && !editForm.rfc.trim()) {
      setEditError('Debe ingresar al menos la Razón Social o el RFC'); return;
    }

    if (editForm.requires_invoice) {
      if (!editForm.rfc.trim()) { setEditError('El RFC es obligatorio cuando el cliente requiere factura'); return; }
      if (!editForm.calle.trim() || !editForm.ciudad.trim() || !editForm.estado.trim()) {
        setEditError('La dirección fiscal es obligatoria cuando el cliente requiere factura'); return;
      }
    }

    const fullAddress = editForm.calle.trim()
      ? `${editForm.calle.trim()}, ${editForm.ciudad.trim()}, ${editForm.estado.trim()}${editForm.cp.trim() ? ', C.P. ' + editForm.cp.trim() : ''}`
      : '';

    try {
      const { error } = await supabase
        .from('erp_clients')
        .update({
          company_name: editForm.company_name.trim() || 'Cliente',
          rfc: editForm.rfc.trim().toUpperCase() || null,
          address: fullAddress || null,
          credit_status: editForm.credit_status,
          credit_limit: parseFloat(editForm.credit_limit) || 0,
          requires_invoice: editForm.requires_invoice,
        })
        .eq('id', cliente.id);

      if (error) throw error;

      setEditSuccess('Cliente actualizado exitosamente');
      await fetchCliente();

      setTimeout(() => {
        setShowEditClientModal(false);
        setEditSuccess('');
      }, 800);

    } catch (err: any) {
      setEditError(err?.message || 'Error al actualizar el cliente. Intente de nuevo.');
    }
  }

  async function handleAddContact() {
    setContactError('');
    setContactSuccess('');

    if (!nuevoContacto.nombre.trim()) { setContactError('El nombre es obligatorio'); return; }
    if (!nuevoContacto.cargo.trim()) { setContactError('El cargo es obligatorio'); return; }
    if (!nuevoContacto.email.trim() && !nuevoContacto.telefono.trim()) {
      setContactError('Debe ingresar al menos un email o teléfono'); return;
    }

    try {
      const { error } = await supabase
        .from('erp_contacts')
        .insert({
          client_id: Number(id),
          name: nuevoContacto.nombre.trim(),
          position: nuevoContacto.cargo.trim(),
          department: nuevoContacto.departamento,
          phone: nuevoContacto.telefono.trim() || null,
          email: nuevoContacto.email.trim() || null,
        });

      if (error) throw error;

      setContactSuccess('Contacto agregado exitosamente');
      await fetchCliente();

      setTimeout(() => {
        setShowContactModal(false);
        setNuevoContacto({ nombre: '', cargo: '', departamento: 'Finanzas', telefono: '', email: '' });
        setContactSuccess('');
      }, 800);

    } catch (err: any) {
      setContactError(err?.message || 'Error al agregar el contacto. Intente de nuevo.');
    }
  }

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  const facturaStatusLabel = (s: string) => {
    if (s === 'paid') return 'Pagado';
    if (s === 'overdue') return 'Vencido';
    if (s === 'parcial') return 'Parcial';
    return 'Pendiente';
  };

  const facturaStatusVariant = (s: string) => {
    if (s === 'paid') return 'success';
    if (s === 'overdue') return 'danger';
    if (s === 'parcial') return 'warning';
    return 'warning';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Cargando cliente...</p>
        </div>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <i className="ri-error-warning-line text-foreground-300 text-5xl"></i>
        <p className="text-foreground-500 mt-4">Cliente no encontrado</p>
        <button onClick={() => navigate('/clientes')} className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer">Volver a Clientes</button>
      </div>
    );
  }

  const porcentajeCredito = Number(cliente.credit_limit) > 0
    ? (Number(cliente.current_balance) / Number(cliente.credit_limit)) * 100
    : 0;

  const statusLabel = (s: string) => {
    if (s === 'active') return 'Activo';
    if (s === 'delinquent') return 'Moroso';
    return 'Inactivo';
  };

  const statusVariant = (s: string) => {
    if (s === 'active') return 'success';
    if (s === 'delinquent') return 'danger';
    return 'neutral';
  };

  const totalEmitido = facturas.reduce((s, f) => s + Number(f.total_amount), 0);
  const totalPagado = pagos.reduce((s, p) => s + Number(p.amount), 0);
  const totalPendiente = facturas.reduce((s, f) => s + Number(f.pending_balance), 0);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/clientes')} className="w-8 h-8 rounded-lg border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-50 transition-colors">
          <i className="ri-arrow-left-line text-foreground-600 text-sm"></i>
        </button>
        <div>
          <h1 className="text-xl font-bold text-foreground-900">{cliente.company_name}</h1>
          <p className="text-sm text-foreground-500">CLI-{String(cliente.id).padStart(3, '0')} · Cliente desde {new Date(cliente.created_at).toLocaleDateString('es-MX')}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={openEditModal}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-foreground-600 bg-white border border-background-200 rounded-lg hover:bg-background-50 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-edit-line"></i>
            Editar
          </button>
          <Badge variant={statusVariant(cliente.status)}>
            {statusLabel(cliente.status)}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Datos Fiscales y Comerciales</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Razón Social</span>
                <p className="text-sm font-medium text-foreground-800 mt-0.5">{cliente.company_name}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">RFC</span>
                <p className="text-sm font-mono text-foreground-700 mt-0.5">{cliente.rfc || '—'}</p>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Facturación</span>
                <p className="text-sm font-medium mt-0.5">
                  <Badge variant={cliente.requires_invoice ? 'info' : 'neutral'}>
                    {cliente.requires_invoice ? 'Requiere Factura' : 'Sin Factura'}
                  </Badge>
                </p>
              </div>
              <div></div>
              <div className="sm:col-span-2">
                <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Dirección Fiscal</span>
                <p className="text-sm text-foreground-700 mt-0.5">{cliente.address || 'No registrada'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Condiciones de Crédito</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-background-50 rounded-lg p-4">
                <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Estatus de Crédito</span>
                <p className="text-sm font-semibold text-foreground-800 mt-1">
                  <Badge variant={cliente.credit_status === '30days' ? 'info' : 'neutral'}>
                    {cliente.credit_status === '30days' ? 'Crédito 30 Días' : 'Sin Crédito'}
                  </Badge>
                </p>
              </div>
              <div className="bg-background-50 rounded-lg p-4">
                <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Límite Autorizado</span>
                <p className="text-lg font-bold text-foreground-900 mt-1">{formatCurrency(Number(cliente.credit_limit))}</p>
              </div>
              <div className="bg-background-50 rounded-lg p-4">
                <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Saldo Pendiente</span>
                <p className={`text-lg font-bold mt-1 ${Number(cliente.current_balance) > Number(cliente.credit_limit) * 0.8 && Number(cliente.credit_limit) > 0 ? 'text-red-600' : 'text-foreground-900'}`}>
                  {formatCurrency(Number(cliente.current_balance))}
                </p>
              </div>
            </div>
            {Number(cliente.credit_limit) > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-foreground-500">Uso de Crédito</span>
                  <span className={`text-xs font-semibold ${porcentajeCredito > 90 ? 'text-red-600' : porcentajeCredito > 75 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {porcentajeCredito.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-background-200 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${porcentajeCredito > 90 ? 'bg-red-500' : porcentajeCredito > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${Math.min(porcentajeCredito, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
            <div className="flex border-b border-background-100">
              <button
                onClick={() => setActiveTab('contactos')}
                className={`flex items-center gap-2 px-5 py-3 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                  activeTab === 'contactos'
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                    : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-50'
                }`}
              >
                <i className="ri-contacts-line"></i>
                Contactos ({contactos.length})
              </button>
              <button
                onClick={() => setActiveTab('historial')}
                className={`flex items-center gap-2 px-5 py-3 text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                  activeTab === 'historial'
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                    : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-50'
                }`}
              >
                <i className="ri-history-line"></i>
                Historial de Pagos y Facturación ({facturas.length + pagos.length})
              </button>
            </div>

            <div className="p-5">
              {activeTab === 'contactos' && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-foreground-900">Contactos del Cliente</h3>
                    <button
                      onClick={() => { setContactError(''); setContactSuccess(''); setShowContactModal(true); }}
                      className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i>
                      Agregar Contacto
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {contactos.map((contacto) => (
                      <div key={contacto.id} className="flex items-start gap-3 p-3 bg-background-50 rounded-lg">
                        <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-700 text-xs font-bold">{contacto.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-foreground-800">{contacto.name}</span>
                          <p className="text-xs text-foreground-500">{contacto.position} · {contacto.department}</p>
                          <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-xs text-foreground-600 flex items-center gap-1">
                              <i className="ri-mail-line text-foreground-400"></i>
                              {contacto.email}
                            </span>
                            <span className="text-xs text-foreground-600 flex items-center gap-1">
                              <i className="ri-phone-line text-foreground-400"></i>
                              {contacto.phone}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {contactos.length === 0 && (
                      <p className="text-xs text-foreground-500 col-span-2">Sin contactos registrados</p>
                    )}
                  </div>

                  <div className="mt-5">
                    <BillingContactsCard
                      clientId={cliente.id}
                      contacts={contactos}
                      onSaved={fetchCliente}
                    />
                  </div>
                </>
              )}

              {activeTab === 'historial' && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-background-50 rounded-lg p-4 text-center">
                      <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Total Emitido</span>
                      <p className="text-lg font-bold text-foreground-900 mt-1">{formatCurrency(totalEmitido)}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 text-center">
                      <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Total Pagado</span>
                      <p className="text-lg font-bold text-emerald-600 mt-1">{formatCurrency(totalPagado)}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 text-center">
                      <span className="text-[10px] uppercase tracking-wider text-foreground-500 font-semibold">Saldo Pendiente</span>
                      <p className="text-lg font-bold text-red-600 mt-1">{formatCurrency(totalPendiente)}</p>
                    </div>
                  </div>

                  {facturas.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                        <i className="ri-file-list-3-line text-foreground-500"></i>
                        Facturas ({facturas.length})
                      </h4>
                      <div className="overflow-x-auto border border-background-200 rounded-lg">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-background-100 bg-background-50">
                              <th className="text-left px-3 py-2 font-semibold text-foreground-500">Folio</th>
                              <th className="text-left px-3 py-2 font-semibold text-foreground-500">Emisión</th>
                              <th className="text-left px-3 py-2 font-semibold text-foreground-500">Vencimiento</th>
                              <th className="text-right px-3 py-2 font-semibold text-foreground-500">Monto</th>
                              <th className="text-right px-3 py-2 font-semibold text-foreground-500">Pendiente</th>
                              <th className="text-center px-3 py-2 font-semibold text-foreground-500">Estatus</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-background-100">
                            {facturas.map((f) => (
                              <tr key={f.id} className="hover:bg-background-50 transition-colors">
                                <td className="px-3 py-2">
                                  <span className="font-mono text-xs font-semibold text-primary-600">{f.folio}</span>
                                </td>
                                <td className="px-3 py-2 text-foreground-600">
                                  {new Date(f.issue_date + 'T00:00:00').toLocaleDateString('es-MX')}
                                </td>
                                <td className="px-3 py-2 text-foreground-600">
                                  {new Date(f.due_date + 'T00:00:00').toLocaleDateString('es-MX')}
                                </td>
                                <td className="px-3 py-2 text-right font-semibold text-foreground-800">{formatCurrency(Number(f.total_amount))}</td>
                                <td className="px-3 py-2 text-right">
                                  <span className={Number(f.pending_balance) > 0 ? 'text-red-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                                    {formatCurrency(Number(f.pending_balance))}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <Badge variant={facturaStatusVariant(f.status)}>{facturaStatusLabel(f.status)}</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {pagos.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                        <i className="ri-money-dollar-circle-line text-foreground-500"></i>
                        Pagos ({pagos.length})
                      </h4>
                      <div className="overflow-x-auto border border-background-200 rounded-lg">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-background-100 bg-background-50">
                              <th className="text-left px-3 py-2 font-semibold text-foreground-500">Factura</th>
                              <th className="text-left px-3 py-2 font-semibold text-foreground-500">Fecha</th>
                              <th className="text-left px-3 py-2 font-semibold text-foreground-500">Método</th>
                              <th className="text-left px-3 py-2 font-semibold text-foreground-500">Referencia</th>
                              <th className="text-right px-3 py-2 font-semibold text-foreground-500">Monto</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-background-100">
                            {pagos.map((p) => (
                              <tr key={p.id} className="hover:bg-background-50 transition-colors">
                                <td className="px-3 py-2">
                                  <span className="font-mono text-xs text-primary-600">{p.invoice_folio}</span>
                                </td>
                                <td className="px-3 py-2 text-foreground-600">
                                  {new Date(p.payment_date + 'T00:00:00').toLocaleDateString('es-MX')}
                                </td>
                                <td className="px-3 py-2 text-foreground-600">{p.payment_method}</td>
                                <td className="px-3 py-2 text-foreground-600 font-mono text-[10px]">{p.reference || '—'}</td>
                                <td className="px-3 py-2 text-right font-semibold text-emerald-600">{formatCurrency(Number(p.amount))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {facturas.length === 0 && pagos.length === 0 && (
                    <div className="py-8 text-center">
                      <i className="ri-bank-card-line text-foreground-300 text-2xl"></i>
                      <p className="text-sm text-foreground-500 mt-2">Sin actividad financiera registrada para este cliente</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <h3 className="text-sm font-semibold text-foreground-900 mb-4">Resumen</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-500">Contactos</span>
                <span className="text-sm font-bold text-foreground-800">{contactos.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-500">Facturas</span>
                <span className="text-sm font-bold text-foreground-800">{facturas.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-500">Saldo Pendiente</span>
                <span className="text-sm font-bold text-primary-600">{formatCurrency(Number(cliente.current_balance))}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-500">Límite de Crédito</span>
                <span className="text-sm font-bold text-foreground-800">{formatCurrency(Number(cliente.credit_limit))}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal open={showEditClientModal} onClose={() => { setShowEditClientModal(false); setEditError(''); setEditSuccess(''); }} title="Editar Cliente" size="lg">
        <div className="space-y-4">
          {editSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{editSuccess}</p>
            </div>
          )}
          {editError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{editError}</p>
            </div>
          )}

          <div className="bg-background-50 rounded-lg p-4 flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-foreground-800">Requiere Factura</span>
              <p className="text-xs text-foreground-500 mt-0.5">Actívelo si el cliente necesita factura fiscal con RFC y dirección</p>
            </div>
            <button
              onClick={() => setEditForm({ ...editForm, requires_invoice: !editForm.requires_invoice })}
              className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${editForm.requires_invoice ? 'bg-emerald-500' : 'bg-background-300'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm ${editForm.requires_invoice ? 'translate-x-6' : 'translate-x-0.5'}`}></span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">
                Razón Social {editForm.requires_invoice && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={editForm.company_name}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">
                RFC {editForm.requires_invoice && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={editForm.rfc}
                onChange={(e) => setEditForm({ ...editForm, rfc: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
                maxLength={13}
              />
            </div>
          </div>

          {editForm.requires_invoice && (
            <div>
              <span className="block text-xs font-semibold text-foreground-700 mb-2">Dirección Fiscal <span className="text-red-500">*</span></span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <input type="text" value={editForm.calle} onChange={(e) => setEditForm({ ...editForm, calle: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                    placeholder="Calle y número" />
                </div>
                <div>
                  <input type="text" value={editForm.ciudad} onChange={(e) => setEditForm({ ...editForm, ciudad: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                    placeholder="Ciudad" />
                </div>
                <div>
                  <input type="text" value={editForm.estado} onChange={(e) => setEditForm({ ...editForm, estado: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                    placeholder="Estado" />
                </div>
                <div>
                  <input type="text" value={editForm.cp} onChange={(e) => setEditForm({ ...editForm, cp: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                    className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                    placeholder="C.P. (opcional)" />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Tipo de Crédito</label>
              <select value={editForm.credit_status} onChange={(e) => setEditForm({ ...editForm, credit_status: e.target.value as 'none' | '30days' })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer">
                <option value="none">Sin Crédito</option>
                <option value="30days">Crédito 30 Días</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Límite de Crédito (MXN)</label>
              <input type="number" value={editForm.credit_limit} onChange={(e) => setEditForm({ ...editForm, credit_limit: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                min="0" step="1000" />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowEditClientModal(false); setEditError(''); setEditSuccess(''); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleEditClient}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-save-line"></i>
              Actualizar Cliente
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showContactModal} onClose={() => { setShowContactModal(false); setContactError(''); setContactSuccess(''); }} title="Agregar Contacto">
        <div className="space-y-4">
          {contactSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{contactSuccess}</p>
            </div>
          )}
          {contactError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{contactError}</p>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Nombre Completo <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={nuevoContacto.nombre}
              onChange={(e) => setNuevoContacto({ ...nuevoContacto, nombre: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              placeholder="Ej. María García López"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Cargo <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={nuevoContacto.cargo}
                onChange={(e) => setNuevoContacto({ ...nuevoContacto, cargo: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="Ej. Gerente de Finanzas"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Departamento</label>
              <select
                value={nuevoContacto.departamento}
                onChange={(e) => setNuevoContacto({ ...nuevoContacto, departamento: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
              >
                <option value="Finanzas">Finanzas</option>
                <option value="Operaciones">Operaciones</option>
                <option value="Soporte">Soporte</option>
                <option value="Ventas">Ventas</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Teléfono</label>
              <input
                type="text"
                value={nuevoContacto.telefono}
                onChange={(e) => setNuevoContacto({ ...nuevoContacto, telefono: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="Ej. 55-1234-5678"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Email</label>
              <input
                type="email"
                value={nuevoContacto.email}
                onChange={(e) => setNuevoContacto({ ...nuevoContacto, email: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="Ej. contacto@empresa.com"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowContactModal(false); setContactError(''); setContactSuccess(''); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleAddContact}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-save-line"></i>
              Guardar Contacto
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}